import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

import { Button } from "@/components/ui/button";

import { SectionCard } from "../../../../components/design-system/admin";
import { useInvitationsApi } from "../../../../common/hooks/use-invitations-api.hook";

/**
 * A super admin never sets someone's password: they send a link and the
 * person chooses one. Using the link signs them out everywhere.
 */
export const UserPasswordReset = ({ user }: { user: { _id?: string; email?: string; firstname?: string; lastname?: string } }) => {
    const { t } = useTranslation();
    const invitationsApi = useInvitationsApi();
    const [sending, setSending] = useState(false);

    const name = [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.email;

    const send = async () => {
        setSending(true);
        try {
            const { emailSent, emailError } = await invitationsApi.sendPasswordReset(user._id);
            if (emailSent) toast.success(t("ADMIN_USERS.RESET.SENT", { email: user.email }));
            else if (emailError === "not_configured") toast.error(t("ADMIN_USERS.RESET.MAIL_NOT_CONFIGURED"));
            else toast.error(t("ADMIN_USERS.RESET.NOT_SENT"));
        } catch (error) {
            console.error("Error sending reset link:", error);
            toast.error(t("ADMIN_USERS.RESET.FAILED"));
        } finally {
            setSending(false);
        }
    };

    return (
        <SectionCard title={t("ADMIN_USERS.RESET.TITLE")} description={t("ADMIN_USERS.RESET.DESCRIPTION", { name })} testId="user-password-reset">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">{t("ADMIN_USERS.RESET.HINT", { email: user?.email })}</p>
                <Button variant="outline" onClick={send} disabled={sending || !user?._id} className="shrink-0">
                    <KeyRound />
                    {t("ADMIN_USERS.RESET.SEND")}
                </Button>
            </div>
        </SectionCard>
    );
};
